import {useMediaQuery} from '@chakra-ui/react';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  AppCarousel,
  Col,
  Row,
  UiBox,
  UiListItem,
  UiQuestionnaireLayout,
  UiText,
  UiUnorderedList,
} from '~/components/elements';
import BoldText from '~/components/shared/BoldText';
import {PAGE_URLS, QUERY_MOBILE} from '~/constants';
import {WEIGHT_LOSS_KEYS, WEIGHT_LOSS_ONE_QUESTIONNAIRES} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {MedProduct} from '~/types/models/questionnaire';
import {getCommaNumber} from '~/utils/helpers';

interface Props {}

const QuestionnaireNutritionPackageScreen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;

  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(
    i => i.key === WEIGHT_LOSS_KEYS.NUTRITION_PACKAGE_SELECTION,
  );

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.NUTRITION_PACKAGE_SELECTION]?.data,
  );

  const collectedData = mdq.collectedData;

  const defaultProduct = questionnaire?.medProducts?.find(i => i.isPopular) || ({} as MedProduct);

  const {inputs, setInputs, validation} = useInput({
    productSku: currentAnswer?.productSku || defaultProduct?.sku || '',
  });

  const selectedItemIndex = questionnaire?.medProducts?.findIndex(i => i.sku === inputs.productSku) || 0;
  const potentialWeightLoss = questionnaire?.medProducts?.[selectedItemIndex]?.potentialWeightLoss || '';

  const onChangeCarousel = (pos: number) => {
    setInputs({
      productSku: questionnaire?.medProducts?.[pos]?.sku || '',
    });
  };

  const onClickSubmit = () => {
    window.scrollTo(0, 0);
    const answerData = {
      key: WEIGHT_LOSS_KEYS.NUTRITION_PACKAGE_SELECTION,
      index: questionnaire?.index || '',
      data: {
        productSku: inputs.productSku,
      },
      order: answerOrder + 1,
    };

    const selectedPackage = {
      key: WEIGHT_LOSS_KEYS.PACKAGE_SELECTION,
      index: questionnaire?.index || '',
      data: {
        productSkus: [inputs.productSku],
      },
      order: answerOrder + 2,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(setQuestionnaireAnswer(selectedPackage));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.NUTRITION_PACKAGE_SELECTION]: answerData,
            [WEIGHT_LOSS_KEYS.PACKAGE_SELECTION]: selectedPackage,
          },
        },
      }),
    );
    history.push(PAGE_URLS.MDQ1_PLAN_WEEKLY_MENU);
  };

  const renderCarouselItem = (products: MedProduct[]) =>
    products?.map((i, index) => {
      const isSelected = inputs.productSku === i?.sku;
      return (
        <Col
          key={index}
          minWidth="30%"
          mx={{base: 2, md: 4}}
          border="1px solid"
          borderRadius="2xl"
          transition="300ms"
          _hover={{cursor: 'pointer'}}
          alignItems="center"
          justifyContent="center"
          minH="315px"
          pt={6}
          pb={4}
          px={{base: 2, lg: 3}}
          bg={i.isPopular ? 'linear-gradient(180deg, #E6F1B4 0%, #EBFFFD 100%);' : undefined}
          boxShadow={isSelected ? '0px 0px 0px 4px #CCDF75' : 'none'}
        >
          {i.isPopular && (
            <Col w="100%" position="absolute" justifyContent="start" alignItems="center" top={-4}>
              <Col bg="lightBaseColor" key={index} px={4} py={1.5} borderRadius="100px">
                <UiText fontSize="sm" fontWeight="600">
                  Most Popular
                </UiText>
              </Col>
            </Col>
          )}

          <UiText fontSize="xl" fontWeight="600">
            {i?.period}
          </UiText>
          <Row alignItems="center">
            <UiText fontSize="2xl" fontWeight="500">
              ${i?.price}
            </UiText>
            {i?.priceSuffix && (
              <UiText fontSize="sm" fontWeight="500" mt={2}>
                &nbsp;/ {i?.priceSuffix}
              </UiText>
            )}
          </Row>
          <Col w="100%" alignItems="flex-start">
            <UiText fontSize="0.625rem" fontWeight="400" mt={2}>
              Includes:
            </UiText>
            <UiUnorderedList textAlign="left">
              {i?.includeItems?.map(desc => (
                <UiListItem key={desc} mb={1}>
                  <BoldText text={desc} fontSize="0.625rem" fontWeight="400" />
                </UiListItem>
              ))}
            </UiUnorderedList>
          </Col>
          <Col>
            <UiText fontSize="0.625rem" fontWeight="400" mb={0} mt={2}>
              total price
            </UiText>
            <UiText fontSize="md" fontWeight="700">
              ${getCommaNumber(i?.totalPrice || 0)}
            </UiText>
          </Col>
        </Col>
      );
    });

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      pageTitle={questionnaire?.title}
      disableNext={!inputs?.productSku}
    >
      <Col alignItems="center" textAlign="center" mt={12} px={2}>
        <UiText fontSize="xl" fontWeight="bold" mb={7}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox w="100%" className="pack-size" mt={8}>
          <AppCarousel
            selectedItem={selectedItemIndex >= 0 ? selectedItemIndex : 0}
            onChange={onChangeCarousel}
            onClickItem={index => onChangeCarousel(index)}
            showIndicators={false}
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            centerMode
            centerSlidePercentage={isDesktop ? 50 : 55}
            emulateTouch
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={50}
          >
            {renderCarouselItem(questionnaire?.medProducts || [])}
          </AppCarousel>
        </UiBox>
        <Col w="60%" alignSelf="center" mt={6} mb={12}>
          <UiText fontWeight="700" mb={2}>
            Potential weight loss*
          </UiText>
          <UiText fontWeight="500" fontSize="2xl">
            {potentialWeightLoss}
          </UiText>
        </Col>
        <UiText fontWeight="500" fontSize="0.625rem" mb={2}>
          *based on past patients’ results. Individual results may vary.
        </UiText>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default QuestionnaireNutritionPackageScreen;
